/**
 * DiffDetailViewer - Inline diff viewer component
 * Extracted from EditDiffMarkersModal to render inline (not as Dialog)
 * Always in edit mode, tracks dirty state for navigation
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DiffImageViewer } from '@/components/diffs/DiffImageViewer';
import { EventRibbon, DiffEvent } from '@/components/diffs/EventRibbon';
import { useToast } from '@/hooks/use-toast';
import { RotateCcw, Loader2 } from 'lucide-react';
import { saveEventAnnotation, resetEventAnnotation, updateEventDescription, updateEventType, markEventAsInvalid } from '@/utils/externalDiffAnnotationsHelper';
import { getEventCategory } from '@/lib/event-type-icons';

const RIBBON_CATEGORY_ORDER: Record<string, number> = { pricing: 0, packaging: 1, product: 2, other: 3 };

type PendingEdit = {
  before?: { x: number; y: number } | null;   // undefined = not edited
  after?: { x: number; y: number } | null;
  beforeVisible?: boolean;
  afterVisible?: boolean;
  viewportDirty?: boolean;
};

interface EventData {
  id: string;
  event_type?: string;
  description?: string;
  annotation?: any;
  invalid?: boolean;
}

interface DiffData {
  primary_original_url?: string;
  secondary_original_url?: string;
  v_before?: string;
  v_after?: string;
  slug: string;
  page?: { company?: { slug?: string } };
  company_logo?: string;
}

interface DiffDetailViewerProps {
  diff: DiffData;
  events: EventData[];
  period: string;
  onDirtyChange?: (dirty: boolean) => void;
  onSave?: () => Promise<void>;
  onSaveAndNext?: () => void;
  onSavePublishAndNext?: () => Promise<void>;
  isPublished?: boolean;
  defaultEditMode?: boolean;
  showInvalidEvents?: boolean;
  onInvalidateEvent?: (eventId: string, hasValidEvents: boolean) => Promise<void>;
  onToggleShowInvalid?: () => void;
  onOpenWebsite?: () => void;
  onOpenPricingPage?: () => void;
  pricingPageUrl?: string | null;
}

export const DiffDetailViewer: React.FC<DiffDetailViewerProps> = ({
  diff,
  events,
  period,
  onDirtyChange,
  onSave,
  onSaveAndNext,
  onSavePublishAndNext,
  isPublished,
  defaultEditMode = true,
  showInvalidEvents = true,
  onInvalidateEvent,
  onToggleShowInvalid,
  onOpenWebsite,
  onOpenPricingPage,
  pricingPageUrl,
}) => {
  const { toast } = useToast();
  const [localEvents, setLocalEvents] = useState(events);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [editMode] = useState(defaultEditMode);
  // Per-event pending edits — keyed by event ID, survives event ribbon navigation
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(new Map());
  const [eventViewports, setEventViewports] = useState<Map<string, { zoom: number; panX: number; panY: number }>>(new Map());
  const [updatingEventType, setUpdatingEventType] = useState(false);
  const [updatingInvalid, setUpdatingInvalid] = useState(false);

  // Sync local events with prop changes
  useEffect(() => {
    setLocalEvents(events);
  }, [events]);

  // Filter events and sort by ribbon category order (left to right) so currentEventIndex matches ribbon position
  const visibleEvents = useMemo(() => {
    const filtered = showInvalidEvents
      ? localEvents
      : localEvents.filter(e => !e.invalid);
    return [...filtered].sort((a, b) => {
      const catA = RIBBON_CATEGORY_ORDER[getEventCategory(a.event_type || '')] ?? 3;
      const catB = RIBBON_CATEGORY_ORDER[getEventCategory(b.event_type || '')] ?? 3;
      return catA - catB;
    });
  }, [localEvents, showInvalidEvents]);

  const currentEvent = visibleEvents[currentEventIndex];
  const slug = diff?.slug || '';
  const companyName = diff?.page?.company?.slug || slug;

  // Derive current-event pending values from the map
  const currentPending = currentEvent?.id ? pendingEdits.get(currentEvent.id) : undefined;
  const editedBeforeMarker = currentPending?.before;
  const editedAfterMarker = currentPending?.after;
  const editedBeforeVisible = currentPending?.beforeVisible
    ?? (currentEvent?.annotation?.markers?.[0]?.hideMarker1 !== true);
  const editedAfterVisible = currentPending?.afterVisible
    ?? (currentEvent?.annotation?.markers?.[0]?.hideMarker2 !== true);
  const hasUnsavedEdits = pendingEdits.size > 0;

  // Notify parent of dirty state changes
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(pendingEdits.size > 0);
    }
  }, [pendingEdits.size, onDirtyChange]);

  // Get image URLs from diff
  const getImageUrls = () => {
    if (!currentEvent) {
      return { before: null, after: null };
    }

    const annotation = currentEvent.annotation;

    let beforeUrl = diff.primary_original_url || null;
    let afterUrl = diff.secondary_original_url || null;

    console.log('[DiffDetailViewer] Getting image URLs:', {
      diff_primary: diff.primary_original_url,
      diff_secondary: diff.secondary_original_url,
      beforeUrl,
      afterUrl,
      annotation_type: annotation?.type,
    });

    // Handle after_only case
    if (!beforeUrl && afterUrl && annotation?.type === 'after_only') {
      beforeUrl = afterUrl;
    }

    // Handle before_only case
    if (beforeUrl && !afterUrl && annotation?.type === 'before_only') {
      afterUrl = beforeUrl;
    }

    return { before: beforeUrl, after: afterUrl };
  };

  // Initialize when event changes (description only — marker state lives in pendingEdits Map)
  useEffect(() => {
    if (currentEvent) {
      setEditedDescription(currentEvent.description || currentEvent.event_type || '');
      setIsEditingDescription(false);

      // Seed viewport from annotation only if not already tracked
      const savedViewport = {
        zoom: currentEvent.annotation?.viewport1?.zoom ?? 1,
        panX: currentEvent.annotation?.viewport1?.panX ?? 0,
        panY: currentEvent.annotation?.viewport1?.panY ?? 0,
      };
      setEventViewports(prev => {
        const newMap = new Map(prev);
        if (!newMap.has(currentEvent.id)) {
          newMap.set(currentEvent.id, savedViewport);
        }
        return newMap;
      });
    }
  }, [currentEventIndex, currentEvent]);

  const handleSaveMarkers = async () => {
    if (pendingEdits.size === 0) {
      toast({ title: 'No changes', description: 'No changes to save', variant: 'default' });
      return;
    }

    setSaving(true);
    try {
      const eventMap = new Map(localEvents.map(e => [e.id, e]));

      const saves = Array.from(pendingEdits.entries()).map(async ([eventId, pending]) => {
        const event = eventMap.get(eventId);
        if (!event) return null;

        const bv = pending.beforeVisible ?? (event.annotation?.markers?.[0]?.hideMarker1 !== true);
        const av = pending.afterVisible ?? (event.annotation?.markers?.[0]?.hideMarker2 !== true);

        const resolveBefore = (fallback: { x: number; y: number } | null | undefined) =>
          pending.before !== undefined ? pending.before : fallback;
        const resolveAfter = (fallback: { x: number; y: number } | null | undefined) =>
          pending.after !== undefined ? pending.after : fallback;

        const existingMarkers = event.annotation?.markers || [];
        const markers = existingMarkers.length > 0
          ? existingMarkers.map((m: any) => ({
              x1: resolveBefore(m.x1 !== undefined ? { x: m.x1, y: m.y1 } : event.annotation?.before_marker)?.x || 0,
              y1: resolveBefore(m.x1 !== undefined ? { x: m.x1, y: m.y1 } : event.annotation?.before_marker)?.y || 0,
              x2: resolveAfter(m.x2 !== undefined ? { x: m.x2, y: m.y2 } : event.annotation?.after_marker)?.x || 0,
              y2: resolveAfter(m.x2 !== undefined ? { x: m.x2, y: m.y2 } : event.annotation?.after_marker)?.y || 0,
              hideMarker1: !bv,
              hideMarker2: !av,
            }))
          : [{
              x1: resolveBefore(event.annotation?.before_marker)?.x || 0,
              y1: resolveBefore(event.annotation?.before_marker)?.y || 0,
              x2: resolveAfter(event.annotation?.after_marker)?.x || 0,
              y2: resolveAfter(event.annotation?.after_marker)?.y || 0,
              hideMarker1: !bv,
              hideMarker2: !av,
            }];

        const viewport = eventViewports.get(eventId) ?? {
          zoom: event.annotation?.viewport1?.zoom ?? 1,
          panX: event.annotation?.viewport1?.panX ?? 0,
          panY: event.annotation?.viewport1?.panY ?? 0,
        };

        const savedBefore = resolveBefore(event.annotation?.before_marker) ?? undefined;
        const savedAfter = resolveAfter(event.annotation?.after_marker) ?? undefined;

        const result = await saveEventAnnotation({
          eventId,
          markers,
          viewport1: viewport,
          viewport2: viewport,
          beforeMarker: savedBefore,
          afterMarker: savedAfter,
        });

        if (!result.success) throw new Error(result.error || 'Failed to save markers');
        return { eventId, markers, viewport, savedBefore, savedAfter };
      });

      const results = (await Promise.all(saves)).filter(Boolean) as NonNullable<Awaited<typeof saves[number]>>[];

      setLocalEvents(prev => prev.map(e => {
        const r = results.find(s => s.eventId === e.id);
        if (!r) return e;
        return {
          ...e,
          annotation: {
            ...e.annotation,
            before_marker: r.savedBefore ?? null,
            after_marker: r.savedAfter ?? null,
            viewport1: r.viewport,
            viewport2: r.viewport,
            markers: r.markers,
          },
        };
      }));

      setPendingEdits(new Map());

      toast({
        title: 'Saved',
        description: results.length === 1 ? 'Changes saved successfully' : `${results.length} events saved`,
      });
    } catch (err) {
      console.error('Error saving markers:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save markers',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNext = async () => {
    await handleSaveMarkers();
    if (onSaveAndNext) {
      onSaveAndNext();
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setShowResetDialog(false);

    try {
      const result = await resetEventAnnotation(currentEvent.id);

      if (!result.success) {
        throw new Error(result.error || 'Failed to reset annotation');
      }

      toast({
        title: 'Reset to defaults',
        description: 'Viewport restored to auto-generated position',
      });

      // Clear stored viewport and pending edits for this event
      setEventViewports(prev => {
        const newMap = new Map(prev);
        newMap.delete(currentEvent.id);
        return newMap;
      });
      setPendingEdits(prev => {
        const m = new Map(prev);
        m.delete(currentEvent.id);
        return m;
      });

      if (onSave) {
        await onSave();
      }
    } catch (err) {
      console.error('Error resetting annotation:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to reset annotation',
        variant: 'destructive',
      });
    } finally {
      setResetting(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!editedDescription.trim() || editedDescription === (currentEvent.description || currentEvent.event_type)) {
      setIsEditingDescription(false);
      return;
    }

    setSavingDescription(true);

    try {
      const result = await updateEventDescription(currentEvent.id, editedDescription.trim());

      if (!result.success) {
        throw new Error(result.error || 'Failed to update description');
      }

      toast({
        title: 'Description updated',
        description: 'Event description saved successfully',
      });

      setIsEditingDescription(false);

      if (onSave) {
        await onSave();
      }
    } catch (err) {
      console.error('Error updating description:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update description',
        variant: 'destructive',
      });
    } finally {
      setSavingDescription(false);
    }
  };

  const handleCancelDescription = () => {
    setEditedDescription(currentEvent.description || currentEvent.event_type || '');
    setIsEditingDescription(false);
  };

  const handleUpdateEventType = async (newType: string) => {
    setUpdatingEventType(true);

    try {
      const result = await updateEventType(currentEvent.id, newType);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update event type');
      }

      toast({
        title: 'Event type updated',
        description: 'Event type changed successfully',
      });

      if (onSave) {
        await onSave();
      }
    } catch (err) {
      console.error('Error updating event type:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update event type',
        variant: 'destructive',
      });
    } finally {
      setUpdatingEventType(false);
    }
  };

  const handleToggleInvalid = async (eventId: string, invalid: boolean) => {
    try {
      setUpdatingInvalid(true);

      // Call Management API
      await markEventAsInvalid(eventId, invalid);

      // Update local events state
      setLocalEvents(prev => prev.map(e =>
        e.id === eventId ? { ...e, invalid } : e
      ));

      // Check if there are any valid events left after this change
      const updatedEvents = localEvents.map(e =>
        e.id === eventId ? { ...e, invalid } : e
      );
      const hasValidEvents = updatedEvents.some(e => !e.invalid);

      // If hiding invalid events and marking as invalid, adjust index if needed
      if (!showInvalidEvents && invalid) {
        const newVisibleEvents = updatedEvents.filter(e => !e.invalid);

        // If current event was filtered out and there are still valid events, select first valid event
        if (newVisibleEvents.length > 0 && currentEventIndex >= newVisibleEvents.length) {
          setCurrentEventIndex(0);
        }
      }

      toast({
        title: invalid ? 'Event marked as invalid' : 'Event marked as valid',
        variant: 'default',
      });

      // Notify parent for auto-navigation handling (only if there are valid events)
      if (onInvalidateEvent && hasValidEvents) {
        await onInvalidateEvent(eventId, hasValidEvents);
      }
    } catch (error) {
      console.error('Failed to toggle event invalid status:', error);
      toast({
        title: 'Error updating event',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setUpdatingInvalid(false);
    }
  };

  const handleMarkerChange = (side: 'before' | 'after', marker: { x: number; y: number } | null) => {
    if (!currentEvent?.id) return;
    setPendingEdits(prev => {
      const m = new Map(prev);
      const cur = m.get(currentEvent.id) ?? {};
      m.set(currentEvent.id, side === 'before' ? { ...cur, before: marker } : { ...cur, after: marker });
      return m;
    });
  };

  const handleVisibilityChange = (side: 'before' | 'after', visible: boolean) => {
    if (!currentEvent?.id) return;
    setPendingEdits(prev => {
      const m = new Map(prev);
      const cur = m.get(currentEvent.id) ?? {};
      m.set(currentEvent.id, side === 'before' ? { ...cur, beforeVisible: visible } : { ...cur, afterVisible: visible });
      return m;
    });
  };

  const handleViewportChange = (side: 'before' | 'after', viewport: { zoom: number; panX: number; panY: number }) => {
    if (!currentEvent?.id) return;
    setEventViewports(prev => {
      const m = new Map(prev);
      m.set(currentEvent.id, viewport);
      return m;
    });
    setPendingEdits(prev => {
      const m = new Map(prev);
      const cur = m.get(currentEvent.id) ?? {};
      m.set(currentEvent.id, { ...cur, viewportDirty: true });
      return m;
    });
  };

  const urls = getImageUrls();

  // Convert events to DiffEvent format for EventRibbon (memoized)
  const ribbonEvents = useMemo<DiffEvent[]>(() => {
    return visibleEvents.map(event => {
      const annotation = event.annotation;

      return {
        id: event.id,
        type: event.event_type || 'other',
        description: event.description || 'No description',
        invalid: event.invalid || false,
        created_at: '',
        updated_at: '',
        url: annotation?.url,
        images: {
          before_cropped: annotation?.images?.before_cropped,
          after_cropped: annotation?.images?.after_cropped,
          before_annotated: annotation?.images?.before_annotated,
          after_annotated: annotation?.images?.after_annotated,
        },
        viewport1: annotation?.viewport1,
        viewport2: annotation?.viewport2,
        before_marker: annotation?.before_marker,
        after_marker: annotation?.after_marker,
      };
    });
  }, [visibleEvents]);

  // Handle edge cases
  if (visibleEvents.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="mb-4 text-lg font-medium">No valid events to display</p>
        <p className="text-sm mb-6">All events have been marked as invalid.</p>
        {onToggleShowInvalid && (
          <Button
            onClick={onToggleShowInvalid}
            variant="outline"
          >
            Show Invalid Events
          </Button>
        )}
      </div>
    );
  }

  if (!urls.before && !urls.after) {
    return (
      <div className="text-center py-12 text-gray-500">
        No images available for this diff
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Event Ribbon */}
      {ribbonEvents.length > 0 && (
        <EventRibbon
          events={ribbonEvents}
          selectedEventIndex={currentEventIndex}
          onEventSelect={setCurrentEventIndex}
          isEditingDescription={isEditingDescription}
          editedDescription={editedDescription}
          onDescriptionChange={setEditedDescription}
          onSaveDescription={handleSaveDescription}
          onCancelDescription={handleCancelDescription}
          onStartEditing={() => setIsEditingDescription(true)}
          savingDescription={savingDescription}
          onEventTypeChange={handleUpdateEventType}
          updatingEventType={updatingEventType}
          slug={slug}
          period={period}
          onMarkInvalid={handleToggleInvalid}
          showInvalidEvents={showInvalidEvents}
          updatingInvalid={updatingInvalid}
          onToggleShowInvalid={onToggleShowInvalid}
          onOpenWebsite={onOpenWebsite}
          onOpenPricingPage={onOpenPricingPage}
          pricingPageUrl={pricingPageUrl}
        />
      )}

      {/* Diff Viewer - with explicit height */}
      <div className="h-[600px] min-h-[600px]">
        {(loading || resetting) ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">{loading ? 'Loading...' : 'Resetting to defaults...'}</p>
            </div>
          </div>
        ) : (
          <DiffImageViewer
            beforeImageUrl={urls.before || ''}
            afterImageUrl={urls.after || ''}
            beforeMarker={currentEvent.annotation?.before_marker}
            afterMarker={currentEvent.annotation?.after_marker}
            beforeViewport={currentEvent.annotation?.viewport1}
            afterViewport={currentEvent.annotation?.viewport2}
            eventKey={currentEventIndex}
            editMode={editMode}
            onMarkerChange={handleMarkerChange}
            onVisibilityChange={handleVisibilityChange}
            onViewportChange={handleViewportChange}
            onSave={handleSaveMarkers}
            initialBeforeVisible={currentEvent.annotation?.markers?.[0]?.hideMarker1 === false || currentEvent.annotation?.markers?.[0]?.hideMarker1 === undefined}
            initialAfterVisible={currentEvent.annotation?.markers?.[0]?.hideMarker2 === false || currentEvent.annotation?.markers?.[0]?.hideMarker2 === undefined}
            initialZoom={eventViewports.get(currentEvent.id)?.zoom}
            pendingBeforeMarker={currentPending?.before}
            pendingAfterMarker={currentPending?.after}
            pendingBeforeVisible={currentPending?.beforeVisible}
            pendingAfterVisible={currentPending?.afterVisible}
          />
        )}
      </div>

      {/* Save Controls - visible when dirty */}
      {hasUnsavedEdits && !loading && !resetting && (
        <div className="flex items-center justify-between gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="text-sm text-yellow-800">
            You have unsaved changes
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowResetDialog(true)}
              disabled={saving}
              className="text-gray-600 hover:text-red-600"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={handleSaveMarkers}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {!isPublished && onSavePublishAndNext ? (
              <Button
                onClick={async () => { await handleSaveMarkers(); await onSavePublishAndNext(); }}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {saving ? 'Saving...' : 'Save & Publish & Next'}
              </Button>
            ) : (
              <Button
                onClick={handleSaveAndNext}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save & Next'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Auto-Generated Defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete your custom marker positions and viewport settings, reverting to the automatically generated annotations from OCR data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              disabled={resetting}
            >
              {resetting ? 'Resetting...' : 'Reset to Defaults'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
